/**
 * @module features/devices/components/DeviceCardList
 * @description Mobile (<640px) stacked summary cards with 0 CLS matching skeleton.
 * Clicking a card opens the View Sheet; tapping "Edit" opens the Edit Sheet directly.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DeviceItem } from "../types";
import { DeviceStatusBadge } from "./DeviceStatusBadge";
import { deviceTypeLabel } from "../utils/format";

interface DeviceCardListProps {
  devices: DeviceItem[];
  onSelect: (device: DeviceItem) => void;
  onEdit?: (device: DeviceItem) => void;
}

export function DeviceCardList({ devices, onSelect, onEdit }: DeviceCardListProps) {
  const navigate = useNavigate();
  return (
    <ul className="sm:hidden space-y-2.5">
      {devices.map((device) => (
        <li key={device.id}>
          <div
            onClick={() => onSelect(device)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(device);
              }
            }}
            className="min-h-[112px] w-full text-left rounded-xl border border-border/80 bg-card p-3 shadow-xs flex flex-col justify-between active:scale-[0.99] transition-transform cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {/* Top Row: Name, MAC, Status */}
            <div className="flex items-start justify-between gap-2.5">
              <div className="min-w-0">
                <h3 className="font-semibold text-sm text-foreground truncate">
                  {device.device_name}
                </h3>
                <p className="text-xs text-muted-foreground font-mono truncate mt-0.5">
                  {device.mac_address || device.registered_device_id}
                </p>
              </div>
              <DeviceStatusBadge status={device.status} className="shrink-0" />
            </div>

            {/* Bottom Row: Metadata & Quick Actions */}
            <div
              className="flex items-center justify-between pt-2 border-t border-border/60 text-xs text-muted-foreground mt-2"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="truncate pr-2 font-mono text-[11px]">
                {deviceTypeLabel(device.device_type)}
                {device.device_type === "display_module" && device.display_module?.os_version
                  ? ` · v${device.display_module.os_version}`
                  : ""}
              </span>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/s-admin/devices/${device.id}/telemetry`)}
                  aria-label={`View telemetry for ${device.device_name}`}
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-emerald-600 touch:min-h-[44px]"
                >
                  <Activity className="h-3.5 w-3.5 mr-1" />
                  Telemetry
                </Button>
                {onEdit && device.status !== "DECOMMISSIONED" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(device)}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground touch:min-h-[44px]"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
